import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Modal from "../../../../../UI/Modal/Modal";
import CloseButton from "../../../../../UI/CloseButton/CloseButton";
import RefreshIcon from "../../../../../Icon-SVG/RefreshIcon";
import SandboxTaskStatusBadge from "../../../../../common/AgentSandboxTasks/SandboxTaskStatusBadge";
import SandboxTaskReportsButton from "../../../../../common/AgentSandboxTasks/SandboxTaskReportsButton";
import {
  TASK_POLL_INTERVAL,
  getTaskErrorMessage,
  isTaskActive,
  shouldPollTask,
} from "../../../../../common/AgentSandboxTasks/constants";
import {
  RequestAgentSandboxTasks,
  RequestCancelAgentSandboxTask,
} from "../../../../../../store/actions/integrationsActions";
import { CopyClipboard } from "../../../../../../store/actions/dashboardActions";

import "./styles.scss";

const PAGE_SIZE = 10;

const shortHash = (hash) => (hash ? `${hash.slice(0, 8)}…${hash.slice(-6)}` : "-");

const getFileName = (filePath = "") => filePath.split(/[\\/]/).pop() || "-";

const getFileDir = (filePath = "") =>
  filePath.slice(0, filePath.length - getFileName(filePath).length);

// Sandbox requests made for one agent from threat hunter detections, with their progress.
const AgentSandboxRequestsModal = ({ agentId, hostname, onClose }) => {
  const dispatch = useDispatch();
  const [pageNumber, setPageNumber] = useState(1);

  const tasks = useSelector((state) => state.integrations.agentSandboxTasks);
  const total = useSelector((state) => state.integrations.agentSandboxTasksTotal);
  const loading = useSelector((state) => state.integrations.agentSandboxTasksLoading);
  const agentOnline = useSelector((state) => state.integrations.agentSandboxTasksAgentOnline);
  const cancelLoading = useSelector((state) => state.integrations.cancelAgentSandboxTaskLoading);

  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const hasChangingTask = tasks.some(shouldPollTask);

  const fetchTasks = useCallback(
    (silent = false) =>
      dispatch(RequestAgentSandboxTasks({ agentId, pageNumber, pageSize: PAGE_SIZE, silent })),
    [dispatch, agentId, pageNumber],
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (!hasChangingTask) return;
    const interval = setInterval(() => fetchTasks(true), TASK_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [hasChangingTask, fetchTasks]);

  const cancelHandler = (taskId) => {
    dispatch(RequestCancelAgentSandboxTask({ taskId, onSuccess: () => fetchTasks(true) }));
  };

  const renderDetail = (task) => {
    if (task.status === "Completed" && task.reportFileID) {
      return <SandboxTaskReportsButton task={task} />;
    }
    if (task.status === "Pending") {
      return agentOnline
        ? "The agent will collect the file on its next poll."
        : "Agent is offline, the request runs when it comes online.";
    }
    if (task.status === "Dispatched") return "The agent is uploading the file.";
    // The path is already in the File column.
    return getTaskErrorMessage(task, { withPath: false }) || "-";
  };

  return (
    <Modal active={true} setActive={onClose} type="agent-sandbox-requests">
      <div className="agent-sandbox-requests__header">
        <h3>
          Sandbox Requests{hostname ? ` - ${hostname}` : ""}
          <span
            className={`agent-sandbox-requests__online ${
              agentOnline ? "agent-sandbox-requests__online--on" : ""
            }`}>
            {agentOnline ? "Online" : "Offline"}
          </span>
        </h3>
        <div className="agent-sandbox-requests__actions">
          <button
            type="button"
            className="agent-sandbox-requests__icon-button"
            title="Refresh"
            onClick={() => fetchTasks()}>
            <RefreshIcon width={18} height={18} />
          </button>
          <CloseButton onClick={onClose} />
        </div>
      </div>

      <div className="agent-sandbox-requests__table-wrapper">
        {loading ? (
          <div className="agent-sandbox-requests__empty">Loading...</div>
        ) : !tasks.length ? (
          <div className="agent-sandbox-requests__empty">No sandbox requests for this agent.</div>
        ) : (
          <table className="agent-sandbox-requests__table">
            {/* Fixed widths, so a long path or message can't stretch the table */}
            <colgroup>
              <col style={{ width: "28%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "19%" }} />
              <col style={{ width: "6%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>File</th>
                <th>SHA256</th>
                <th>Requested By</th>
                <th>Requested At</th>
                <th>Status</th>
                <th>Detail</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id}>
                  <td title={task.filePath}>
                    <div className="agent-sandbox-requests__file-name">
                      {getFileName(task.filePath)}
                    </div>
                    <div className="agent-sandbox-requests__file-dir">
                      {getFileDir(task.filePath)}
                    </div>
                  </td>
                  <td>
                    <span
                      className="agent-sandbox-requests__hash"
                      title={`${task.sha256} (click to copy)`}
                      onClick={() => dispatch(CopyClipboard(task.sha256))}>
                      {shortHash(task.sha256)}
                    </span>
                  </td>
                  <td>{task.createdBy || "-"}</td>
                  <td>{task.createdAt ? `${task.createdAt} UTC` : "-"}</td>
                  <td>
                    <SandboxTaskStatusBadge task={task} />
                  </td>
                  <td>
                    <div
                      className="agent-sandbox-requests__detail"
                      title={
                        isTaskActive(task) ? undefined : getTaskErrorMessage(task) || undefined
                      }>
                      {renderDetail(task)}
                    </div>
                  </td>
                  <td>
                    {task.status === "Pending" ? (
                      <button
                        type="button"
                        className="agent-sandbox-requests__cancel"
                        disabled={cancelLoading}
                        onClick={() => cancelHandler(task.id)}>
                        Cancel
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pageCount > 1 ? (
        <div className="agent-sandbox-requests__pagination">
          <button
            type="button"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((page) => page - 1)}>
            Previous
          </button>
          <span>
            {pageNumber} / {pageCount}
          </span>
          <button
            type="button"
            disabled={pageNumber >= pageCount}
            onClick={() => setPageNumber((page) => page + 1)}>
            Next
          </button>
        </div>
      ) : null}
    </Modal>
  );
};

export default AgentSandboxRequestsModal;
